export const ACCESS_DENIED_EVENT = "sensor:access-denied";

export const ACCESS_ACTIONS = ["read", "create", "update", "delete"];

export const CUD_ACTIONS = ["create", "update", "delete"];

export const ACCESS_MODULE_KEYS = [
  "invitations",
  "reports",
  "observations",
  "users",
  "companies",
  "report_templates",
  "notification_templates",
  "email_templates",
  "custom_texts",
  "editable_values",
  "database",
  "gallery",
  "main_algorithm",
  "mobile_apps",
  "advanced",
];

/**
 * Builds a module → action map from user_access_context rows.
 * @param {Array<object>} userAccessContexts
 * @returns {object}
 */
export const grantsFromContexts = (userAccessContexts) => {
  const grants = {};
  (userAccessContexts || []).forEach((row) => {
    if (!row || row.active === false) {
      return;
    }
    const context = row.accessContext || row.access_context;
    if (!context || !context.module || !context.type) {
      return;
    }
    if (!grants[context.module]) {
      grants[context.module] = {};
    }
    grants[context.module][context.type] = true;
  });
  return grants;
};

/**
 * Returns whether the user may perform a module action.
 * @param {object|null} user
 * @param {string} moduleName
 * @param {string} action
 * @returns {boolean}
 */
export const userCan = (user, moduleName, action) => {
  const role = user && user.role && user.role.role;
  if (role === "ADMIN") {
    return true;
  }
  if (role !== "ASSISTANT") {
    return false;
  }
  const grants = grantsFromContexts(user.userAccessContexts);
  const moduleGrants = grants[moduleName] || {};
  if (!moduleGrants.read) {
    return false;
  }
  if (action === "read") {
    return true;
  }
  return Boolean(moduleGrants[action]);
};

/**
 * Stable string of active grants, used to skip no-op user refreshes.
 * @param {Array<object>|undefined} userAccessContexts
 * @returns {string}
 */
export const grantsSignature = (userAccessContexts) =>
  JSON.stringify(grantsFromContexts(userAccessContexts));

/**
 * Returns whether the current session is an assistant (not admin).
 * @param {object|null} user
 * @param {string|undefined} role
 * @returns {boolean}
 */
const isAssistantSession = (user, role) => {
  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") {
    return false;
  }
  if (normalizedRole === "assistant") {
    return true;
  }
  return Boolean(user && user.role && user.role.role === "ASSISTANT");
};

/**
 * Whether the session may update a module. Admins always can;
 * assistants need the update grant after user context loads.
 * @param {object|null} user
 * @param {string|undefined} role
 * @param {string} moduleName
 * @returns {boolean}
 */
export const canUpdateModule = (user, role, moduleName) => {
  if (user && user.role && user.role.role === "ADMIN") {
    return true;
  }
  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") {
    return true;
  }
  if (!isAssistantSession(user, role)) {
    return false;
  }
  return userCan(user, moduleName, "update");
};

/**
 * Whether the session may create in a module. Admins always can;
 * assistants need the create grant after user context loads.
 * @param {object|null} user
 * @param {string|undefined} role
 * @param {string} moduleName
 * @returns {boolean}
 */
export const canCreateModule = (user, role, moduleName) => {
  if (user && user.role && user.role.role === "ADMIN") {
    return true;
  }
  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") {
    return true;
  }
  if (!isAssistantSession(user, role)) {
    return false;
  }
  return userCan(user, moduleName, "create");
};

/**
 * Whether the session may delete in a module. Admins always can;
 * assistants need the delete grant after user context loads.
 * @param {object|null} user
 * @param {string|undefined} role
 * @param {string} moduleName
 * @returns {boolean}
 */
export const canDeleteModule = (user, role, moduleName) => {
  if (user && user.role && user.role.role === "ADMIN") {
    return true;
  }
  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") {
    return true;
  }
  if (!isAssistantSession(user, role)) {
    return false;
  }
  return userCan(user, moduleName, "delete");
};

/**
 * Route path → module used by the page guard and menu.
 */
export const ROUTE_MODULES = [
  { match: /^\/admin\/users\/access-context/, adminOnly: true },
  { match: /^\/admin\/welcome/, allowed: true },
  { match: /^\/admin\/invites/, module: "invitations" },
  { match: /^\/user\/[^/]+\/answer-questionnaire\/history/, module: "invitations" },
  { match: /^\/user\/[^/]+\/answer-questionnaire/, module: "invitations", action: "create" },
  { match: /^\/user\/[^/]+\/results/, module: "reports" },
  { match: /^\/admin\/app\/[^/]+\/results/, module: "reports" },
  { match: /^\/admin\/app\/[^/]+\/report\//, module: "reports" },
  { match: /^\/admin\/report\//, module: "reports" },
  { match: /^\/admin\/print\//, module: "reports" },
  { match: /^\/admin\/pacients/, module: "users" },
  { match: /^\/admin\/prescribers/, module: "users" },
  { match: /^\/admin\/users\/backoffice/, module: "users" },
  { match: /^\/admin\/users\/app/, module: "users" },
  { match: /^\/admin\/user\//, module: "users" },
  { match: /^\/admin\/companies/, module: "companies" },
  { match: /^\/admin\/reports/, module: "report_templates" },
  { match: /^\/admin\/email-template/, module: "email_templates" },
  { match: /^\/admin\/push-notifications/, module: "notification_templates" },
  { match: /^\/admin\/custom-texts/, module: "custom_texts" },
  { match: /^\/admin\/backoffice-values/, module: "editable_values" },
  { match: /^\/admin\/questionnaires/, module: "database" },
  { match: /^\/admin\/questions/, module: "database" },
  { match: /^\/admin\/answers-options/, module: "database" },
  { match: /^\/admin\/text-config/, module: "database" },
  { match: /^\/admin\/app\/[^/]+\/text-config/, module: "database" },
  { match: /^\/admin\/gallery/, module: "gallery" },
  { match: /^\/admin\/criteria-usage/, module: "main_algorithm" },
  { match: /^\/admin\/criteria-management/, module: "advanced" },
  { match: /^\/admin\/criteria/, module: "main_algorithm" },
  { match: /^\/admin\/meta-skill/, module: "main_algorithm" },
  { match: /^\/admin\/principle/, module: "main_algorithm" },
  { match: /^\/admin\/algorithm-config/, module: "main_algorithm" },
  { match: /^\/admin\/elements/, module: "main_algorithm" },
  { match: /^\/admin\/element\//, module: "main_algorithm" },
  { match: /^\/admin\/mobile-apps/, module: "mobile_apps" },
  { match: /^\/admin\/app\//, module: "mobile_apps" },
  { match: /^\/admin\/feelings/, module: "advanced" },
  { match: /^\/admin\/organs/, module: "advanced" },
  { match: /^\/admin\/seasons/, module: "advanced" },
  { match: /^\/admin\/natural-elements/, module: "advanced" },
  { match: /^\/admin\/characters/, module: "advanced" },
  { match: /^\/admin\/input\/create/, module: "advanced", action: "create" },
  { match: /^\/admin\/algorithm-output\/create/, module: "advanced", action: "create" },
  { match: /^\/admin\/general-output\/create/, module: "advanced", action: "create" },
  { match: /^\/admin\/input/, module: "advanced" },
  { match: /^\/admin\/algorithm-output/, module: "advanced" },
  { match: /^\/admin\/general-output/, module: "advanced" },
];

/**
 * Resolves the module required for a frontend path.
 * @param {string} pathname
 * @returns {{allowed?: boolean, adminOnly?: boolean, module?: string, action?: string}|null}
 */
export const resolvePathPermission = (pathname) => {
  const rule = ROUTE_MODULES.find((entry) => entry.match.test(pathname || ""));
  return rule || null;
};

/**
 * Returns whether the current session may open a backoffice path.
 * Admins always pass. Assistants need an explicit allow rule and read grant.
 * Unmapped /admin/* paths are denied for assistants.
 * @param {object|null} user
 * @param {string|undefined} role
 * @param {string} pathname
 * @returns {boolean}
 */
export const canAccessAdminPath = (user, role, pathname) => {
  if (user && user.role && user.role.role === "ADMIN") {
    return true;
  }

  const normalizedRole = (role || "").toLowerCase();
  if (normalizedRole === "admin") {
    return true;
  }

  const isAssistant =
    normalizedRole === "assistant" ||
    (user && user.role && user.role.role === "ASSISTANT");

  if (!isAssistant) {
    return true;
  }

  const permission = resolvePathPermission(pathname || "");

  if (permission && permission.adminOnly) {
    return false;
  }

  if (permission && permission.allowed) {
    return true;
  }

  if (permission && permission.module) {
    return userCan(user, permission.module, permission.action || "read");
  }

  if ((pathname || "").indexOf("/admin/") === 0) {
    return false;
  }

  return true;
};
